import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import {
    getAccount,
    getToken,
    getWallet,
    removeAccount,
    removeToken,
    removeWallet,
    setAccount,
    setToken,
    setWallet,
    WalletEnum
} from 'shared/config'
import { useLoginModal, useMemoizedFn, useMountedPromiseFn } from 'shared/hooks'
import { useSolana, useWeb3 } from 'wallet'
import * as services from './data-access'
import { actions, selectors } from './state'

export const useAuth = () => {
    const dispatch = useDispatch()
    const {
        methods: { hide }
    } = useLoginModal()
    const {
        hasSolana,
        methods: { connectPhantom, disconnectPhantom }
    } = useSolana()
    const {
        hasEthereum,
        methods: { connectMetaMask, disconnectMetaMask }
    } = useWeb3()

    const [loading, setLoading] = useState<boolean>(false)

    const signInApi = useMountedPromiseFn(services.signIn)
    const signIn = useMemoizedFn((account: string) => {
        setLoading(true)
        return signInApi(account)
            .then(({ access_token }) => {
                setToken(access_token)
                setAccount(account)
                dispatch(actions.setUserAccessToken({ userAccessToken: access_token }))
                dispatch(actions.setUserAccount({ userAccount: account }))
                setLoading(false)
                return { access_token }
            })
            .catch(err => {
                setLoading(false)
                return Promise.reject(err)
            })
    })

    const account = useSelector(selectors.getUserAccount)

    const connect = useMemoizedFn(async (wallet: WalletEnum) => {
        let accountRes = ''
        switch (wallet) {
            case WalletEnum.METAMASK:
                accountRes = await connectMetaMask()
                break
            case WalletEnum.PHANTOM:
                accountRes = await connectPhantom()
                break
            default:
                break
        }
        await signIn(accountRes)
        setWallet(wallet)
        hide()
    })

    const disconnect = useMemoizedFn(async () => {
        switch (getWallet()) {
            case WalletEnum.METAMASK:
                await disconnectMetaMask()
                break
            case WalletEnum.PHANTOM:
                await disconnectPhantom()
                break
            default:
                break
        }
        removeUserInfo()
    })

    const removeUserInfo = useMemoizedFn(() => {
        removeWallet()
        removeToken()
        removeAccount()
        dispatch(actions.clearUserAccessToken())
        dispatch(actions.clearUserAccount())
    })

    const initUserInfo = useMemoizedFn(() => {
        getToken() && dispatch(actions.setUserAccessToken({ userAccessToken: getToken() }))
        getAccount() && dispatch(actions.setUserAccount({ userAccount: getAccount() }))
        getToken() && getAccount() && getWallet() && connect(getWallet() as WalletEnum)
    })

    return {
        account,
        loading,
        hasSolana,
        hasEthereum,
        methods: { initUserInfo, removeUserInfo, connect, disconnect }
    }
}
